import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  FormLabel,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Switch,
  Text,
  HStack,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import { getData, postData, putData, deleteData } from '../../../utils/httpClient';
import AdminPageShell from '../../../components/layout/AdminPageShell';

const emptyForm = {
  mode: 'day',
  date: '',
  startDate: '',
  endDate: '',
  name: '',
  enabled: true,
};

export default function HolidaysAdmin() {
  const [holidays, setHolidays] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const res = await getData('/holidays?all=1');
      setHolidays(res?.payload?.holidays || []);
    } catch {
      toast({
        title: 'Error al cargar días bloqueados',
        status: 'error',
        duration: 3000,
      });
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = (mode = 'day') => {
    setEditing(null);
    setForm({ ...emptyForm, mode });
    onOpen();
  };

  const openEdit = (h) => {
    setEditing(h);
    setForm({
      mode: 'day',
      date: String(h.date).slice(0, 10),
      startDate: '',
      endDate: '',
      name: h.name || '',
      enabled: h.enabled !== false,
    });
    onOpen();
  };

  const save = async () => {
    if (!form.name?.trim()) {
      toast({ title: 'Complete el motivo', status: 'warning', duration: 3000 });
      return;
    }

    const isPeriod = !editing && form.mode === 'period';

    if (isPeriod) {
      if (!form.startDate || !form.endDate) {
        toast({
          title: 'Complete fecha inicio y fin',
          status: 'warning',
          duration: 3000,
        });
        return;
      }
      if (form.endDate < form.startDate) {
        toast({
          title: 'La fecha fin debe ser ≥ fecha inicio',
          status: 'warning',
          duration: 3000,
        });
        return;
      }
    } else if (!form.date) {
      toast({ title: 'Complete la fecha', status: 'warning', duration: 3000 });
      return;
    }

    setSaving(true);
    try {
      if (editing?.id) {
        await putData(`/holidays/${editing.id}`, {
          date: form.date,
          name: form.name.trim(),
          enabled: form.enabled,
        });
        toast({ title: 'Día actualizado', status: 'success', duration: 2500 });
      } else if (isPeriod) {
        const res = await postData('/holidays/period', {
          startDate: form.startDate,
          endDate: form.endDate,
          name: form.name.trim(),
          enabled: form.enabled,
        });
        const created = res?.payload?.created ?? 0;
        const skipped = res?.payload?.skipped ?? 0;
        toast({
          title: 'Período bloqueado',
          description: `${created} día(s) creado(s)${
            skipped ? `, ${skipped} ya existían` : ''
          }`,
          status: 'success',
          duration: 4000,
        });
      } else {
        await postData('/holidays', {
          date: form.date,
          name: form.name.trim(),
          enabled: form.enabled,
        });
        toast({ title: 'Día bloqueado', status: 'success', duration: 2500 });
      }
      onClose();
      load();
    } catch (e) {
      toast({
        title: e?.response?.data?.message || e?.message || 'Error al guardar',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este día bloqueado?')) return;
    try {
      await deleteData(`/holidays/${id}`);
      toast({ title: 'Eliminado', status: 'success', duration: 2500 });
      load();
    } catch {
      toast({ title: 'Error al eliminar', status: 'error', duration: 3000 });
    }
  };

  return (
    <AdminPageShell
      title="Días bloqueados (agenda)"
      actions={
        <HStack spacing={2}>
          <Button size="sm" colorScheme="gray" onClick={() => openNew('day')}>
            + Bloquear día
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => openNew('period')}
          >
            + Bloquear período
          </Button>
        </HStack>
      }
    >
      <Box
        borderWidth="1px"
        borderRadius="md"
        overflowX="hidden"
        maxW="100%"
        sx={{
          '& table': { width: '100%', tableLayout: 'fixed' },
          '& th': {
            whiteSpace: 'normal',
            overflow: 'visible',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            verticalAlign: 'bottom',
            lineHeight: '1.25',
          },
          '& td': {
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            maxWidth: 0,
            verticalAlign: 'middle',
          },
          '& th:last-child, & td:last-child': {
            width: '6.5rem',
            minWidth: '6.5rem',
            maxWidth: '6.5rem',
            whiteSpace: 'nowrap',
            overflow: 'visible',
            textAlign: 'right',
          },
        }}
      >
        <Table size="sm">
          <Thead bg="gray.100">
            <Tr>
              <Th>Fecha</Th>
              <Th>Motivo</Th>
              <Th>Activo</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {holidays.length === 0 ? (
              <Tr>
                <Td colSpan={4} color="gray.500">
                  No hay días bloqueados
                </Td>
              </Tr>
            ) : (
              holidays.map((h) => (
                <Tr key={h.id}>
                  <Td>{String(h.date).slice(0, 10)}</Td>
                  <Td>{h.name}</Td>
                  <Td>{h.enabled ? 'Sí' : 'No'}</Td>
                  <Td>
                    <Button
                      size="xs"
                      variant="ghost"
                      mr={1}
                      onClick={() => openEdit(h)}
                    >
                      Editar
                    </Button>
                    <Button
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => remove(h.id)}
                    >
                      Eliminar
                    </Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">
            {editing
              ? 'Editar día bloqueado'
              : form.mode === 'period'
                ? 'Bloquear período en agenda'
                : 'Bloquear día en agenda'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!editing && (
              <>
                <FormLabel fontSize="sm">Tipo</FormLabel>
                <RadioGroup
                  mb={3}
                  value={form.mode}
                  onChange={(mode) => setForm({ ...form, mode })}
                >
                  <Stack direction="row" spacing={4}>
                    <Radio value="day" size="sm">
                      Un día
                    </Radio>
                    <Radio value="period" size="sm">
                      Período
                    </Radio>
                  </Stack>
                </RadioGroup>
              </>
            )}

            {form.mode === 'period' && !editing ? (
              <>
                <FormLabel fontSize="sm">Desde</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  mb={3}
                  value={form.startDate}
                  onChange={(e) =>
                    setForm({ ...form, startDate: e.target.value })
                  }
                />
                <FormLabel fontSize="sm">Hasta</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  mb={3}
                  value={form.endDate}
                  onChange={(e) =>
                    setForm({ ...form, endDate: e.target.value })
                  }
                />
              </>
            ) : (
              <>
                <FormLabel fontSize="sm">Fecha</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  mb={3}
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </>
            )}

            <FormLabel fontSize="sm">
              Motivo (ej. Feriado, Vacaciones, Taller cerrado)
            </FormLabel>
            <Input
              size="sm"
              mb={3}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Switch
              isChecked={form.enabled}
              onChange={(e) =>
                setForm({ ...form, enabled: e.target.checked })
              }
            >
              <Text as="span" fontSize="sm" ml={2}>
                Activo (bloquea agenda)
              </Text>
            </Switch>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={2} onClick={onClose}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={save}
              isLoading={saving}
            >
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminPageShell>
  );
}
